/**
 * lib/badges/from-row.ts — SERVER adapter: a loose Supabase product row (+ its
 * offers) → the canonical `Badge[]` array, via the pure resolver in ./resolve.
 *
 * This is what the API routes (products, collections, search) call to emit
 * `badges: Badge[]` on each product (docs/Page-Types-And-Templates.md §6). It is the
 * server sibling of components/badges/from-product.ts (which adapts client widget
 * shapes through the legacy `determineBadges` engine); both funnel into the one
 * canonical rule set so a product renders identical badges everywhere.
 *
 * OWNED-DATA-ONLY / REAL-OR-HIDDEN: every field is read straight off the row —
 * nothing is fabricated. A badge only appears when the row's real columns support
 * it (spec §4.1). In particular:
 *   - rating/reviews come ONLY from products.specs_json (never a synthetic
 *     placeholder some surfaces generate for display) — so top_rated/premium fire
 *     on real review data or not at all.
 *   - best_seller/trending need products.popularity_score / popularity_velocity,
 *     which are materialized by the DORMANT cron (item 64). Until that column
 *     exists + is populated the fields are absent, `signals` is omitted, and those
 *     two badges simply never fire (honest: no analytics loaded → no analytics
 *     badge). When the staged migration lands and a route SELECTs the columns,
 *     they flow through automatically — no code change here.
 *
 * Pure + framework-free (no Supabase / React / I/O) so it stays unit-testable.
 */

import {normalizeListedOffer} from './price-evidence'
import {
  resolveBadges,
  type Badge,
  type ResolveOfferInput,
  type ResolveSignalsInput,
} from './resolve'

/** A retailer-offer row in any of the shapes the routes carry (snake or camel). */
export interface OfferRowish {
  price_cents?: number | null
  priceCents?: number | null
  original_price_cents?: number | null
  originalPriceCents?: number | null
  deal_badge?: string | null
  dealBadge?: string | null
  deal_ends_at?: string | null
  dealEndsAt?: string | null
  availability_max_qty?: number | null
  availabilityMaxQty?: number | null
  in_stock?: boolean | null
  inStock?: boolean | null
  status?: string | null
  priceStale?: boolean
  isPriceUnknown?: boolean
}

/** A product row carrying the resolver-relevant columns (all optional). */
export interface ProductRowish {
  lowest_price_cents?: number | null
  average_price_30d_cents?: number | null
  lowest_price_ever_cents?: number | null
  created_at?: string | null
  specs_json?: {
    rating?: number | null
    reviews?: number | null
    energy_star?: boolean | null
    [key: string]: unknown
  } | null
  /**
   * products.tags — an 'energy-star' entry is a REAL owned-data signal (set by
   * the feed pipeline) that some rows carry instead of specs_json.energy_star.
   * Either real source may light the badge; nothing is fabricated.
   */
  tags?: string[] | null
  // Materialized by the DORMANT item-64 cron; absent until the migration lands.
  popularity_score?: number | null
  popularity_velocity?: number | null
  offers?: OfferRowish[] | null
  retailer_offers?: OfferRowish[] | null
}

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

/** Normalize a mixed-shape offer row into the resolver's offer input. */
function toResolveOffer(o: OfferRowish): ResolveOfferInput {
  return {
    priceStale: o.priceStale,
    isPriceUnknown: o.isPriceUnknown,
    inStock: o.in_stock ?? o.inStock,
    status: o.status,
    priceCents: o.price_cents ?? o.priceCents ?? null,
    originalPriceCents: normalizeListedOffer(o)?.originalPriceCents ?? null,
    dealBadge: o.deal_badge ?? o.dealBadge ?? null,
    dealEndsAt: o.deal_ends_at ?? o.dealEndsAt ?? null,
    availabilityMaxQty: o.availability_max_qty ?? o.availabilityMaxQty ?? null,
  }
}

/**
 * Pick the single best offer for badge purposes: the lowest-priced in-stock,
 * active offer (mirrors how the routes already order/pick offers). Returns null
 * when there is no usable offer, in which case offer-derived badges (deal,
 * low_stock) simply don't fire.
 */
export function pickBestOffer(
  offers: OfferRowish[] | null | undefined
): ResolveOfferInput | null {
  if (!offers || offers.length === 0) return null
  const usable = offers.filter((o) => {
    const inStock = o.in_stock ?? o.inStock
    const active = o.status == null || o.status === 'active'
    return inStock !== false && active
  })
  const pool = usable.length > 0 ? usable : offers
  let best: OfferRowish | null = null
  let bestPrice = Number.POSITIVE_INFINITY
  for (const o of pool) {
    const price = num(o.price_cents ?? o.priceCents)
    // Prefer the lowest positive price; fall back to the first offer if none
    // carry a price (so deal/low_stock can still resolve off a priceless offer).
    if (price !== undefined && price > 0 && price < bestPrice) {
      best = o
      bestPrice = price
    }
  }
  if (!best) best = pool[0]
  return toResolveOffer(best)
}

/**
 * Resolve the canonical `Badge[]` for one product row. Pass `offersOverride` when
 * the best offer is already known; otherwise the best in-stock offer is picked
 * from `row.offers` / `row.retailer_offers`.
 */
export function resolveBadgesForRow(
  row: ProductRowish,
  offersOverride?: OfferRowish[] | ResolveOfferInput | null
): Badge[] {
  const specs = row.specs_json ?? undefined

  let bestOffer: ResolveOfferInput | null
  if (offersOverride && !Array.isArray(offersOverride)) {
    bestOffer = offersOverride
  } else {
    const offers =
      (offersOverride as OfferRowish[] | null | undefined) ??
      row.offers ??
      row.retailer_offers ??
      null
    bestOffer = pickBestOffer(offers)
  }

  // Only pass analytics signals when the materialized columns are actually
  // present on the row — otherwise best_seller/trending stay dark (Real-or-Hidden).
  const popularityScore = num(row.popularity_score)
  const popularityVelocity = num(row.popularity_velocity)
  const signals: ResolveSignalsInput | undefined =
    popularityScore !== undefined || popularityVelocity !== undefined
      ? { popularityScore, popularityVelocity }
      : undefined

  return resolveBadges({
    product: {
      lowestPriceCents: row.lowest_price_cents ?? null,
      averagePrice30dCents: row.average_price_30d_cents ?? null,
      lowestPriceEverCents: row.lowest_price_ever_cents ?? null,
      createdAt: row.created_at ?? null,
      rating: num(specs?.rating) ?? null,
      reviews: num(specs?.reviews) ?? null,
      energyStar:
        specs?.energy_star === true ||
        (Array.isArray(row.tags) && row.tags.includes('energy-star')),
    },
    bestOffer,
    signals,
  })
}
